/**
 * Maps technical pipeline / Nutrient errors to Coach-friendly copy for
 * `collectionMeta.error`, realtime status messages, and builder toasts.
 *
 * Raw errors remain in logs, Sentry, and upload telemetry — only user-facing
 * surfaces should call these helpers (plus a display pass for legacy meta).
 */

export type UserFacingDocumentProcessingErrorKind =
  | 'instant_finalize'
  | 'detection'
  | 'processing_failed'
  | 'needs_reupload'
  | 'generic';

const NUTRIENT_API_ERROR = /^Nutrient API error:\s*\d+/i;
const DOCUMENT_ENGINE_FAILED = /^Document Engine failed/i;

const DEFAULT_BY_KIND: Record<UserFacingDocumentProcessingErrorKind, string> = {
  instant_finalize: 'We had trouble placing auto-detected fields on this PDF.',
  detection:
    "We couldn't auto-detect fields in this document. You can add them manually.",
  processing_failed: 'We had trouble parsing this document.',
  needs_reupload: 'Uploaded file could not be read. Please try again.',
  generic: 'Document processing failed. Please try again.',
};

/** Coach/toast copy when the V1 processing soft-gate fail-open fires (~80s). */
export function buildProcessingSoftGateFailOpenUserMessage(): string {
  return 'Field detection is taking longer than expected. You can keep editing this PDF and add fields manually. Use Re-run field detection in Deal Coach if it still looks stuck.';
}

/** Shown when instant finalize fails; includes keep-editing guidance for toasts. */
export function buildInstantFinalizeFailedUserMessage(
  rawErrorMessage: string | undefined
): string {
  const primary = toUserFacingDocumentProcessingError(
    rawErrorMessage,
    'instant_finalize'
  );
  const lower = primary.toLowerCase();
  if (lower.includes('keep editing') || lower.includes('add fields manually')) {
    return primary;
  }
  return `${primary} You can keep editing and add fields manually.`;
}

export function isTechnicalDocumentProcessingError(
  message: string | undefined | null
): boolean {
  const trimmed = message?.trim() ?? '';
  if (trimmed.length === 0) {
    return true;
  }
  if (NUTRIENT_API_ERROR.test(trimmed)) {
    return true;
  }
  if (DOCUMENT_ENGINE_FAILED.test(trimmed)) {
    return true;
  }
  if (trimmed.startsWith('{') && trimmed.includes('"error"')) {
    return true;
  }
  return false;
}

function isPreservedUserCopy(message: string): boolean {
  if (isTechnicalDocumentProcessingError(message)) {
    return false;
  }
  const lower = message.toLowerCase();
  return (
    lower.includes('please try again') ||
    lower.includes('please re-upload') ||
    lower.includes('could not be read') ||
    lower.includes('exceeds the') ||
    lower.includes('keep editing') ||
    lower.includes('add fields manually') ||
    lower.includes("couldn't") ||
    lower.includes('we had trouble') ||
    lower.includes('we couldn')
  );
}

/**
 * Returns Coach-friendly copy for a raw pipeline error string.
 * Non-technical messages that already read as user copy are preserved.
 */
export function toUserFacingDocumentProcessingError(
  raw: string | null | undefined,
  kind: UserFacingDocumentProcessingErrorKind = 'generic'
): string {
  const trimmed = raw?.trim();
  if (trimmed && isPreservedUserCopy(trimmed)) {
    return trimmed;
  }
  if (trimmed && !isTechnicalDocumentProcessingError(trimmed)) {
    return trimmed;
  }
  return DEFAULT_BY_KIND[kind];
}

/** Resolve persisted collection meta errors for banners, toasts, and hooks. */
export function resolveUserFacingCollectionMetaError(
  meta:
    | {
        error?: string | null;
        detectionFailed?: boolean;
        status?: string;
        needsReupload?: boolean;
      }
    | undefined
    | null
): string | undefined {
  if (!meta?.error?.trim()) {
    if (meta?.detectionFailed) {
      return DEFAULT_BY_KIND.detection;
    }
    if (meta?.needsReupload) {
      return DEFAULT_BY_KIND.needs_reupload;
    }
    if (meta?.status === 'failed') {
      return DEFAULT_BY_KIND.processing_failed;
    }
    return undefined;
  }

  if (meta.detectionFailed) {
    return toUserFacingDocumentProcessingError(meta.error, 'detection');
  }
  if (meta.needsReupload) {
    return toUserFacingDocumentProcessingError(meta.error, 'needs_reupload');
  }
  if (meta.status === 'failed') {
    return toUserFacingDocumentProcessingError(meta.error, 'processing_failed');
  }
  return toUserFacingDocumentProcessingError(meta.error, 'generic');
}
